/**
 * The pre-event swipe deck: profiles one decision at a time (PreSpark /
 * NoPreSpark), paged five at a time as the deck runs low. Shared by the /spark
 * tab (GET /user/next-profiles, everyone) and an event's "Spark before you go"
 * screen (GET /spark/:sparkId/next-profiles, that event's attendees only).
 *
 * The hook owns the deck's screens: callers spread `deckScreens(renderCard)`
 * into a ScreenTransitioner wired to `screenTransitionerRef`, and add their own
 * "noMoreProfiles" empty state. An event's deck also adds "notRegistered", for
 * when the server says the viewer holds no seat there (or the event is over).
 */

import { Box } from "@chakra-ui/react";
import { useReq } from "@larner.dev/use-req";
import { UserProfile, UserRelationshipType } from "@greenflagdate/shared";
import {
  ReactNode,
  Reducer,
  useCallback,
  useEffect,
  useReducer,
  useRef,
} from "react";
import { apiReq } from "./apiReq";
import { store, useStoreData } from "./store/store";
import { ActionType } from "./store/storeActions";
import type { ScreenTransitionerHandle } from "../components/ScreenTransitioner/ScreenTransitioner";
import type { MatchProfileDecision } from "../pages/marketing/Match/MatchProfile";
import { useMatchCelebration } from "../components/MatchNotification/useMatchCelebration";
import { LoaderScreen } from "../components/LoaderScreen/LoaderScreen";
import { NotFound } from "../components/NotFound/NotFound";
import { getMissingProfileSteps } from "../components/CompleteProfileGate/CompleteProfileGate";

const PAGE_SIZE = 5;

// By default, once a user has PreSpark'ed this many people we require a fully
// complete profile before they can keep swiping — at this point they're likely
// to start matching and the other side needs something to look at.
const FULL_PROFILE_GATE_THRESHOLD = 3;

// What CompleteProfileGate asks for over the deck: the fields the deck needs to
// pick profiles, then the full profile once the threshold is reached.
const SWIPE_PROFILE_STEPS = ["gender", "genderPreference", "birthDate"] as const;
const FULL_PROFILE_STEPS = [
  "photo",
  "name",
  "birthDate",
  "gender",
  "genderPreference",
] as const;
const gateStepsAt = (numRecentLikes: number, fullProfileGateThreshold: number) =>
  numRecentLikes >= fullProfileGateThreshold
    ? FULL_PROFILE_STEPS
    : SWIPE_PROFILE_STEPS;

/**
 * The deck answers these when the viewer's profile is too thin to build one.
 * The CompleteProfileGate overlay asks for what is missing.
 */
export const isIncompleteProfileError = (code: string | undefined) =>
  code === "MISSING_GENDER" || code === "MISSING_GENDER_PREFERENCE";

// Decisions made in any deck this session. A deck that stays mounted while
// another one is used (the /spark tab under an event's deck) compares this
// when it is shown again, so it never offers someone already decided on.
let decisionsMade = 0;

export interface ProfilesState {
  currentProfile?: UserProfile;
  nextProfiles: UserProfile[];
  moreProfilesToLoad: boolean;
  numRecentLikes: number;
}

enum SwipeActionType {
  SetInitialProfiles,
  NextProfile,
  AddProfiles,
}

type Action =
  | { type: SwipeActionType.SetInitialProfiles; profiles: UserProfile[] }
  | { type: SwipeActionType.NextProfile; relationship: UserRelationshipType }
  | { type: SwipeActionType.AddProfiles; profiles: UserProfile[] };

const reducer: Reducer<ProfilesState, Action> = (currentState, action) => {
  if (action.type === SwipeActionType.SetInitialProfiles) {
    const [currentProfile, ...nextProfiles] = action.profiles;
    return {
      currentProfile,
      nextProfiles,
      moreProfilesToLoad: action.profiles.length === PAGE_SIZE,
      numRecentLikes: 0,
    };
  } else if (action.type === SwipeActionType.NextProfile) {
    const [currentProfile, ...nextProfiles] = currentState.nextProfiles;
    return {
      currentProfile,
      nextProfiles,
      moreProfilesToLoad: currentState.moreProfilesToLoad,
      numRecentLikes:
        currentState.numRecentLikes +
        (action.relationship === UserRelationshipType.PreSpark ? 1 : 0),
    };
  } else if (action.type === SwipeActionType.AddProfiles) {
    return {
      ...currentState,
      nextProfiles: [...currentState.nextProfiles, ...action.profiles],
      moreProfilesToLoad: action.profiles.length === PAGE_SIZE,
    };
  }
  return currentState;
};

export interface UseProfileDeckOptions {
  /** Narrow the deck to one event's attendees. */
  sparkId?: number;
  /**
   * Whether to load the deck. It loads once this is true and the viewer has
   * everything CompleteProfileGate asks for before the first card.
   */
  enabled: boolean;
  /**
   * Whether the deck is on screen (default true). A deck that stays mounted
   * while hidden reloads when it is shown again if the viewer decided on
   * anyone in another deck meanwhile.
   */
  shown?: boolean;
  /**
   * Called after each PreSpark lands, with the session's PreSpark count
   * including this one.
   */
  onPreSpark?: (numRecentLikes: number) => void;
  /**
   * PreSparks since the deck last loaded before CompleteProfileGate asks for
   * the full profile (photo and name too). Default FULL_PROFILE_GATE_THRESHOLD; 0 asks
   * before the first card.
   */
  fullProfileGateThreshold?: number;
}

export const useProfileDeck = ({
  sparkId,
  enabled,
  shown = true,
  onPreSpark,
  fullProfileGateThreshold = FULL_PROFILE_GATE_THRESHOLD,
}: UseProfileDeckOptions) => {
  const storeData = useStoreData();
  const [profilesReq, profilesReqState] = useReq<UserProfile[]>(apiReq);
  const [profilesState, dispatch] = useReducer(reducer, {
    nextProfiles: [],
    moreProfilesToLoad: true,
    numRecentLikes: 0,
  });
  // The /user/relationship PUT optionally returns a flushed copy of the
  // viewer's likes_attending_sparks_cache when the privacy gate decides to
  // rotate it (see User.maybeFlushLikesAttendingSparksCache on the server).
  const [relationshipReq] = useReq<{
    likesAttendingSparksUpdate: {
      likes_attending_sparks_cache: Record<string, number>;
    } | null;
    // Set by the server when this PreSpark completed a two-way pre-event
    // spark. Drives the mutual-spark celebration toast.
    mutualMatch?: boolean;
  }>(apiReq);
  const { celebrate, matchCelebration } = useMatchCelebration();
  const screenTransitionerRef = useRef<ScreenTransitionerHandle>(null);
  // `decisionsMade` as of this deck's last load, plus its own decisions since.
  const decisionsSeen = useRef(decisionsMade);

  const profilesPath = useCallback(
    (excludeUserIds: number[] = []) => {
      const path = sparkId
        ? `/spark/${sparkId}/next-profiles`
        : "/user/next-profiles";
      return excludeUserIds.length
        ? `${path}?excludeUserIds=${excludeUserIds.join(",")}`
        : path;
    },
    [sparkId]
  );

  const setRelationship = useCallback(
    async (decision: MatchProfileDecision) => {
      if (!profilesState.currentProfile) {
        return;
      }

      // Snapshot the swiped profile before the deck advances — the toast needs
      // their name/photo, and `currentProfile` moves on below.
      const swipedProfile = profilesState.currentProfile;
      const relationship =
        decision === "match"
          ? UserRelationshipType.PreSpark
          : UserRelationshipType.NoPreSpark;
      const result = await relationshipReq.put("/user/relationship", {
        userId: swipedProfile.id,
        relationship,
      });
      if (!result.success) return;
      decisionsMade += 1;
      decisionsSeen.current += 1;
      if (result.result.mutualMatch) {
        celebrate(swipedProfile);
      }
      // Always merge a returned cache update into the store so any consumer
      // sees fresh per-spark counts. The server may flush the cache on any
      // swipe past the privacy threshold.
      if (result.result.likesAttendingSparksUpdate && storeData.user) {
        store.dispatch({
          type: ActionType.UpdateUser,
          params: {
            user: {
              ...storeData.user,
              ...result.result.likesAttendingSparksUpdate,
            },
          },
        });
      }
      if (relationship === UserRelationshipType.PreSpark) {
        onPreSpark?.(profilesState.numRecentLikes + 1);
      }
      dispatch({ type: SwipeActionType.NextProfile, relationship });
      if (
        profilesState.nextProfiles.length === 2 &&
        profilesState.moreProfilesToLoad
      ) {
        profilesReq
          .get(profilesPath(profilesState.nextProfiles.map((p) => p.id)))
          .then((response) => {
            if (response.success) {
              dispatch({
                type: SwipeActionType.AddProfiles,
                profiles: response.result,
              });
            }
          });
      }
      if (profilesState.nextProfiles.length) {
        // Advance instantly — the MatchProfile card has already played its
        // swipe-off animation, and the next profile's hero is visible in
        // the behind-card layer. Sliding the new screen in on top would
        // read as a redundant "slide from the right".
        screenTransitionerRef.current?.setIndex(
          `user_${profilesState.nextProfiles[0].id}`,
          { noAnimate: true }
        );
      } else {
        screenTransitionerRef.current?.setIndex("noMoreProfiles");
      }
    },
    [
      profilesReq,
      profilesPath,
      profilesState.currentProfile,
      profilesState.moreProfilesToLoad,
      profilesState.nextProfiles,
      profilesState.numRecentLikes,
      relationshipReq,
      storeData.user,
      celebrate,
      onPreSpark,
    ]
  );

  const getNextProfiles = useCallback(() => {
    decisionsSeen.current = decisionsMade;
    profilesReq.get(profilesPath()).then((response) => {
      let screen = null;
      if (response.success) {
        dispatch({
          type: SwipeActionType.SetInitialProfiles,
          profiles: response.result,
        });
        screen = response.result.length
          ? `user_${response.result[0].id}`
          : "noMoreProfiles";
      } else {
        // MISSING_GENDER / MISSING_GENDER_PREFERENCE are handled by the
        // CompleteProfileGate overlay; stay on the loader until the gate
        // closes and the user re-fetches with a complete-enough profile.
        if (isIncompleteProfileError(response.error.code)) {
          screen = "loader";
        } else if (response.error.code === "NOT_REGISTERED") {
          screen = "notRegistered";
        } else {
          screen = response.error.code === "NOT_FOUND" ? "notFound" : "error";
        }
      }
      screenTransitionerRef.current?.setIndex(screen, { noAnimate: true });
    });
  }, [profilesReq, profilesPath]);

  const user = storeData.user;
  // Hold the deck until the gate has nothing to ask before the first card, so
  // no profile is fetched or rendered behind it.
  const requiredReady =
    getMissingProfileSteps(user, [...gateStepsAt(0, fullProfileGateThreshold)])
      .length === 0;
  useEffect(() => {
    if (!enabled || !requiredReady) return;
    getNextProfiles();
  }, [getNextProfiles, enabled, requiredReady]);
  useEffect(() => {
    if (!shown || !enabled || !requiredReady) return;
    if (decisionsSeen.current === decisionsMade) return;
    // Someone in this deck's queue may have been decided on elsewhere: start
    // over from the server, which leaves them out.
    screenTransitionerRef.current?.setIndex("loader", { noAnimate: true });
    getNextProfiles();
  }, [getNextProfiles, shown, enabled, requiredReady]);

  const gateRequiredSteps = gateStepsAt(
    profilesState.numRecentLikes,
    fullProfileGateThreshold
  );

  /**
   * Every screen the deck moves between except "noMoreProfiles": the loader,
   * the error states, and the current and next profile cards (the next one is
   * mounted early so the swipe can advance without a slide).
   */
  const deckScreens = (
    renderCard: (profile: UserProfile, behind: UserProfile | null) => ReactNode
  ): Record<string, ReactNode> => {
    const { currentProfile, nextProfiles } = profilesState;
    return {
      loader: <LoaderScreen />,
      notFound: <NotFound />,
      error: (
        <Box
          padding="1rem"
          borderRadius="md"
          borderWidth="1px"
          borderColor="red.solid"
          color="red.fg"
        >
          {profilesReqState.error?.code}
        </Box>
      ),
      ...(currentProfile && {
        [`user_${currentProfile.id}`]: renderCard(
          currentProfile,
          nextProfiles[0] ?? null
        ),
      }),
      ...(nextProfiles[0] && {
        [`user_${nextProfiles[0].id}`]: renderCard(
          nextProfiles[0],
          nextProfiles[1] ?? null
        ),
      }),
    };
  };

  return {
    setRelationship,
    screenTransitionerRef,
    deckScreens,
    gateRequiredSteps,
    matchCelebration,
  };
};
